import { add, canvasOf, place } from './common';

/**
 * **LP の増減の数字**（damage・pay_lpcost・recover が共有する。Issue #554 / #579 / #585）。
 *
 * 銀の金属文字＋黒の縁（`kit.metalTextTexture`、`edge: null`。先頭に「-」。recover は「+」）の後ろに、硬い斬り傷の差し
 * （平行四辺形の太い線・細い線・黒の影の 3 本）を置く。**描き方は同じで、違うのは色と大きさだけ**（ユーザー 2026-09-28:
 * pay_lpcost のフォント・文字の感じは damage の数字と揃える）。
 */

/** 差しの色（`deep` 太い線 / `light` 細い線 / `shadow` 影） */
export const DAMAGE_SLASH = Object.freeze({ deep: '#c8101e', light: '#ff2e3c', shadow: '#1a0406' });
export const COST_SLASH = Object.freeze({ deep: '#d86a00', light: '#ff9a2e', shadow: '#1f0d02' });
/** 回復（recover）の差し。緑 */
export const RECOVER_SLASH = Object.freeze({ deep: '#0f9a3c', light: '#3cf07a', shadow: '#03170a' });

/** 数字のテクスチャ（`{ texture, width, height }`）。符号は既定で「-」（damage / pay_lpcost）、回復は `'+'` */
export const lifeNumberTexture = (kit, amount, size, sign = '-') => kit.metalTextTexture(`${sign}${amount}`, { size, edge: null });

/** 文字の形の幅（テクスチャの余白を除く。`metalTextTexture` の余白の決め方と同じ） */
export function lifeNumberGlyphWidth(tex, size) {
  const pad = Math.max(60, size * 0.14 + size * 0.11 * 2 + 10);
  return Math.max(0, tex.width - pad * 2);
}

/**
 * 数字 1 枚を `root` に足す。返す `g` を動かせば数字と差しが一緒に動く。
 * `lay(k)` は差しを幅 k（0〜1）で置き直す（出方で差しを伸ばすとき。既定は 1 で置いてある）。
 */
export function makeLifeNumber(ctx, root, tex, { order = 10, colors = DAMAGE_SLASH, name = 'life-number' } = {}) {
  const { THREE, kit } = ctx;
  const nw = tex.width; const nh = tex.height;
  const bar = (color) => {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), kit.plain(null, 1));
    m.material.color?.set?.(color);
    return m;
  };
  const g = new THREE.Group();
  add(root, g, order);
  const slashes = [
    { m: add(g, bar(colors.deep), order), y: nh * 0.16, w: nw * 0.92, h: nh * 0.11, rot: -0.1, skew: 0 },
    { m: add(g, bar(colors.light), order + 0.001), y: nh * 0.26, w: nw * 0.62, h: nh * 0.045, rot: -0.1, skew: 0 },
    { m: add(g, bar(colors.shadow), order - 0.001), y: nh * 0.16 + 6, w: nw * 0.92, h: nh * 0.11, rot: -0.1, skew: 10 },
  ];
  const lay = (k = 1) => {
    slashes.forEach((s) => place(s.m, s.skew, s.y, { sx: Math.max(0.001, s.w * k), sy: s.h, rot: s.rot }));
  };
  lay(1);
  const num = add(g, kit.quad(kit.plain(tex.texture, 1), nw, nh), order + 0.01);
  num.name = name;
  return { g, num, slashes, lay };
}

/**
 * **LP の数字のすぐ下の置き場所**（舞台のピクセル。pay_lpcost・recover が共有）。LP の数字の箱の下端の 6px 下に上端、
 * 外側（P1 は左・P2 は右）を箱の外側にそろえる。返すのは数字の中心と外側の向き（P1 = -1 左 / P2 = +1 右）。
 */
export function tagPlace(ctx, player, tagW, tagH) {
  const p = player === 1 ? 1 : 0;
  const { w: W } = canvasOf(ctx);
  const b = ctx.stage?.hud?.life?.(p) ?? { x: p === 1 ? W - 420 : 220, y: 100, w: 200, h: 80 };
  const out = p === 1 ? 1 : -1;
  const edge = p === 1 ? b.x + b.w : b.x;
  const M = 12;
  let cx = edge - out * (tagW / 2);
  cx = Math.max(M + tagW / 2, Math.min(W - M - tagW / 2, cx));
  const cy = b.y + b.h + 6 + tagH / 2;
  return { x: cx, y: cy, out };
}
